import { useEffect } from 'react';
import api from '@mysimpledb/shared/config/axios';

const SYNC_DEBUG = process.env.REACT_APP_SYNC_DEBUG === 'true';

const syncDebug = (...args) => {
  if (SYNC_DEBUG) console.log(...args);
};

export default function DesktopSyncProcessor({ token, userId }) {
  const isDesktopSyncAvailable = !!window.syncApi;

  useEffect(() => {
    if (!isDesktopSyncAvailable || !token || !userId) return;

    let cancelled = false;
    let running = false;
    let lockToken = null;
    let renewTimer = null;
    let uploadTimer = null;

    async function checkPendingUploads() {
      if (running || !lockToken) return;
      running = true;

      try {
        const pending = await window.syncApi.listPendingUploads(userId);

        syncDebug('[sync-upload] polling check:', {
          count: pending?.length || 0,
          cancelled,
        });

        if (cancelled || !pending?.length) return;

        for (const item of pending) {
          const localFile = await window.syncApi.readLocalFile(item.localPath);

          const binary = atob(localFile.dataBase64);
          const bytes = new Uint8Array(binary.length);

          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }

          const file = new File([bytes], localFile.name, {
            type: item.mimetype || 'application/octet-stream',
            lastModified: localFile.lastModified,
          });

          file.localPath = localFile.localPath;
          file.attachmentId = item.attachmentId;

          const formData = new FormData();
          formData.append('file', file);

          const response = await api.put(
            `/attachments/${item.attachmentId}/replace`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );

          syncDebug('[sync-upload] upload result:', response.data);

          await api.post(`/attachments/${item.attachmentId}/sync-info`, {
            sync_enabled: true,
            sync_status: 'synced',
            sync_device_id: item.deviceId || null,
            sync_device_name: item.deviceName || null,
            last_local_modified_at: localFile.lastModified
              ? new Date(localFile.lastModified).toISOString()
              : new Date().toISOString(),
          });

          syncDebug('[sync-info] updated:', item.attachmentId);

          await window.syncApi.trackAttachment({
            ...item,
            userId,
            lastSeenMtime: localFile.lastModified,
            lastSeenSize: localFile.size,
            syncStatus: 'synced',
            pendingUpload: false,
          });

          window.dispatchEvent(
            new CustomEvent('attachments:changed', {
              detail: { attachmentId: item.attachmentId },
            })
          );

          syncDebug('[sync-upload] marked synced:', item.attachmentId);
        }
      } catch (err) {
        console.error('[sync-upload] failed:', err);
      } finally {
        running = false;
      }
    }

    async function checkDirtySyncInfo() {
      if (!lockToken) return;
      if (!window.syncApi.listDirtySyncInfo) return;

      try {
        const dirtyItems = await window.syncApi.listDirtySyncInfo(userId);

        if (cancelled || !dirtyItems?.length) return;

        for (const item of dirtyItems) {
          await api.post(
            `/attachments/${item.attachmentId}/sync-info`,
            {
              sync_enabled: true,
              sync_status: item.syncStatus,
              sync_device_id: item.deviceId || null,
              sync_device_name: item.deviceName || null,
              last_local_modified_at: item.lastSeenMtime
                ? new Date(item.lastSeenMtime).toISOString()
                : null,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          await window.syncApi.markSyncInfoClean(item.attachmentId, userId);

          syncDebug('[sync-info] dirty status updated:', {
            attachmentId: item.attachmentId,
            syncStatus: item.syncStatus,
          });
        }
      } catch (err) {
        console.error('[sync-info] dirty update failed:', err);
      }
    }

    async function checkSyncWork() {
      await checkPendingUploads();
      await checkDirtySyncInfo();
    }

    async function startLockedSync() {
      try {
        const lock = await window.syncApi.acquireLock(userId);

        if (cancelled) return;

        // Another app owns active lock.
        // Retry later.
        if (
          !lock?.acquired &&
          lock?.existing?.owner_app &&
          lock?.existing?.owner_app !== 'desktop'
        ) {
          syncDebug('[sync-lock] owned by another app:', lock?.existing);

          setTimeout(() => {
            if (!cancelled) startLockedSync();
          }, 10000);

          return;
        }

        // Already owned by this desktop instance.
        if (lock?.alreadyOwned) {
          syncDebug('[sync-lock] already owned:', lock);

          lockToken = lock.lockToken;
        } else {
          lockToken = lock.lockToken;

          syncDebug('[sync-lock] acquired:', lock);
        }

        await window.syncApi.refreshWatcher(userId);

        // IMPORTANT:
        // Run recovery scan after takeover.
        await window.syncApi.scanLocalChanges(userId);

        renewTimer = setInterval(async () => {
          try {
            if (!lockToken) return;

            const ok = await window.syncApi.renewLock(userId, lockToken);

            if (!ok) {
              console.warn('[sync-lock] renewal failed; stopping sync');

              lockToken = null;

              if (uploadTimer) {
                clearInterval(uploadTimer);
                uploadTimer = null;
              }

              // Retry takeover.
              setTimeout(() => {
                if (!cancelled) startLockedSync();
              }, 10000);
            }
          } catch (err) {
            console.error('[sync-lock] renewal error:', err);
          }
        }, 10000);

        // IMPORTANT:
        // Restart upload processor after takeover.
        await checkSyncWork();

        uploadTimer = setInterval(checkSyncWork, 3000);
      } catch (err) {
        console.error('[sync-lock] acquire failed:', err);

        // Retry later.
        setTimeout(() => {
          if (!cancelled) startLockedSync();
        }, 10000);
      }
    }

    startLockedSync();

    return () => {
      cancelled = true;

      if (renewTimer) clearInterval(renewTimer);
      if (uploadTimer) clearInterval(uploadTimer);

      const tokenToRelease = lockToken;
      lockToken = null;

      if (tokenToRelease && window.syncApi?.releaseLock) {
        window.syncApi
          .releaseLock(userId, tokenToRelease)
          .catch((err) => console.error('[sync-lock] release failed:', err));
      }
    };
  }, [isDesktopSyncAvailable, token, userId]);

  return null;
}
